import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  FiZap, FiPlus, FiTrash2, FiSend, FiCheck, FiAlertCircle, FiLoader, FiSmartphone,
  FiPaperclip, FiImage, FiGrid, FiX, FiMic, FiSquare, FiDatabase, FiEye,
  FiRefreshCw, FiClock, FiEdit3, FiDownload, FiUsers, FiTarget,
  FiMessageSquare, FiTag, FiMinus,
} from 'react-icons/fi'
import type { IconType } from 'react-icons'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import { consumeSse } from '../lib/oracleAiJob'

// Agente de Campanha — uma conversa por campanha.
//
// A COLUNA DA DIREITA É A IDEIA INTEIRA. Já houve um chat de IA neste painel
// (removido em 18/05/2026): ele falhava porque o MODELO era quem tinha de lembrar
// template + público + mapeamento ao mesmo tempo, e perdia o fio. Aqui o estado
// mora no servidor e fica à vista: a Ficha diz o que já tem e o que falta, então
// nem você nem o agente precisam confiar em memória.
//
// Três colunas, cada uma com rolagem própria — a página nunca rola.

type Anexo = {
  id: string; tipo: 'imagem' | 'planilha'; nome: string
  origem?: 'draco' | string
  url?: string; mime?: string
  colunas?: string[]; linhas?: number; total?: number
  colunaTelefone?: string | null; colunaDDD?: string | null
  invalidos?: number; duvidosos?: number; fixos?: number; corrigidos?: number
  leitura?: string; aviso?: string | null
}

type Ficha = {
  objetivo: string | null
  publico: { descricao: string; total: number | null; fonte: string | null; anexoId?: string | null; colunas?: string[] } | null
  template: {
    nome: string; idioma: string; status: string | null; categoria: string | null
    variaveis: string[]; formato: string; temCabecalho: boolean; tipoCabecalho: string | null
  } | null
  variaveis: Record<string, { tipo: string; valor: string }> | null
  imagem: { id: string; url: string; nome: string; tipo: string | null } | null
  teste: { numero: string; comoFoiDito: string; enviadoEm: string | null } | null
  destino: { url: string } | null
}

type BotaoTpl = { tipo: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'; texto: string; url?: string; telefone?: string }

// O que o agente propôs e ainda NÃO foi para a Meta. Enquanto isto existe, nada
// foi cadastrado — a separação entre proposta e criado é a trava do servidor,
// e a tela só reflete ela.
type TemplateProposto = {
  nome: string; categoria: string; idioma: string
  corpo: string; rodape: string | null
  cabecalho: { tipo: string; anexoId: string; nome: string } | null
  botoes: BotaoTpl[]; variaveis: number
  avisos: string[]; propostoEm: string
}

type TemplateCriado = TemplateProposto & {
  metaId: string; status: string; motivoRejeicao: string | null
  // `categoria` é o que pedimos; `categoriaFinal` é o que a Meta decidiu. Elas
  // divergem quando a Meta sobe UTILITY→MARKETING — e é a final que ela paga.
  categoriaFinal: string | null
  confirmadoPorNome: string | null; confirmadoEm: string
}

// O que a conta do disparo devolve — a mesma passada que monta a fila, então o
// número da tela e o número que sai são o mesmo por construção.
type Disparo = {
  template: string | null; destino: string | null; lista: string | null
  testeEnviadoEm: string | null
  vao: number; esperam: number; aptos: number; linhas: number
  fora: Record<string, number> | null
  semDadoColunas: string[]
  lotesJaFeitos: number
  limite: { configurado: boolean; teto: number | null; usados: number; cabemHoje: number | null } | null
  // Quando a janela de 24h vira e a próxima leva cabe. Null = não há espera.
  liberaEm?: string | null
  impedimentos: string[]; avisos: string[]; pode: boolean
}

type RegistroDisparo = {
  campanhaId: string; rotulo: string; quantidade: number
  lote: number; esperam: number; quando: string; porNome: string | null
}

type Chat = {
  id: string; titulo: string; criadoEm: string; atualizadoEm: string
  campaignId: string | null; disparada: boolean
  anexos: Anexo[]
  templateProposto: TemplateProposto | null
  templatesCriados: TemplateCriado[]
  templateChecadoEm?: string | null
  templateChecagemErro?: string | null
  testes?: Array<{ numero: string; comoFoiDito: string; template: string; quando: string; avisos: string[] }>
  disparos?: RegistroDisparo[]
  ficha: Ficha; completa: boolean; falta: string[]
}

// O que a Meta limita: PESSOAS diferentes por janela corrida de 24h. `usados` é
// medido pelo que saiu por ESTA stack — envio por fora não aparece.
type Limite = {
  configurado: boolean
  limite?: number; usados: number; alcance: number
  restam: number | null; cabe: boolean | null
  lotes: { total: number; hoje: number; porDia: number; sobra: number } | null
  liberaEm?: string | null
}

type Turno = {
  id: string; role: string; content: string; quando: string
  meta?: { recusados?: Array<{ campo: string; motivo: string }>; leituras?: string[]; aviso?: string | null } | null
}

// A tela inteira evita o vocabulário da Meta: quem usa isto é recruta de
// Tupperware, não gente de plataforma. "Template" vira "a mensagem" e
// "imagem do cabeçalho" vira "a arte" — os mesmos nomes que a Ficha usa, pra o
// que falta e o campo que falta serem a MESMA palavra.
const ROTULO_FALTA: Record<string, string> = {
  objetivo: 'o objetivo da campanha',
  publico: 'quem vai receber',
  template: 'a mensagem',
  imagem: 'a arte do topo',
  'imagem:tipo': 'a arte do tipo certo (a mensagem pede vídeo e a arte é foto, ou o contrário)',
  planilha: 'a planilha (um dado da mensagem aponta pra coluna dela)',
  'template:aprovacao': 'a aprovação da mensagem na Meta',
}
const rotuloFalta = (f: string) =>
  ROTULO_FALTA[f] ?? (f.startsWith('variavel:') ? `a variável "${f.slice(9)}"` : f)

const COR_STATUS: Record<string, string> = {
  APPROVED: 'text-emerald-600 dark:text-emerald-400',
  PENDING: 'text-amber-600 dark:text-amber-400',
  REJECTED: 'text-red-600 dark:text-red-400',
}
// APPROVED/PENDING/REJECTED é o que a Meta devolve; não é o que se lê.
const ROTULO_STATUS: Record<string, string> = {
  APPROVED: 'aprovada',
  PENDING: 'em análise',
  REJECTED: 'recusada',
}

export function AgenteCampanhaPanel() {
  // `isAdmin` decide se o microfone aparece: /api/whisper/transcribe é
  // adminMiddleware, e oferecer o botão a quem vai levar 403 só produz um
  // "Falha na transcrição: HTTP 403" que não explica nada a quem usa.
  const { authenticatedFetch, user } = useAuth()
  const podeFalar = !!user?.isAdmin

  const [desenvolvimento, setDesenvolvimento] = useState<Chat[]>([])
  const [completas, setCompletas] = useState<Chat[]>([])
  const [atual, setAtual] = useState<Chat | null>(null)
  const [turnos, setTurnos] = useState<Turno[]>([])
  const [entrada, setEntrada] = useState('')
  const [etapa, setEtapa] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  const [anexando, setAnexando] = useState(false)
  const [tplOcupado, setTplOcupado] = useState<'confirmando' | 'checando' | 'testando' | null>(null)
  // Respostas prontas do último turno. Somem assim que ela responde — botão de
  // uma pergunta velha embaixo de uma conversa nova confunde mais do que ajuda.
  const [opcoes, setOpcoes] = useState<string[]>([])
  const [limite, setLimite] = useState<Limite | null>(null)
  // A conta do disparo, buscada sob demanda. Não vem junto do chat de propósito:
  // é uma varredura da planilha inteira, e ninguém precisa dela até querer mandar.
  const [disparo, setDisparo] = useState<Disparo | null>(null)
  const [disparoOcupado, setDisparoOcupado] = useState<'contando' | 'disparando' | null>(null)
  const [semTesteOk, setSemTesteOk] = useState(false)
  // Faz o "conferido há X" envelhecer na tela sem precisar de nova checagem.
  // Sem isto o rótulo congela em "agora mesmo" e volta a não dizer nada.
  const [, tique] = useState(0)
  useEffect(() => { const id = setInterval(() => tique(n => n + 1), 30000); return () => clearInterval(id) }, [])
  const [criandoChat, setCriandoChat] = useState(false)

  // ── Três colunas não cabem em toda tela ───────────────────────────────────
  //
  // As laterais eram `w-[250px]` e `w-[296px]`, ambas `shrink-0`, e o arquivo
  // não tinha UM breakpoint: 546px de lateral fixa em qualquer largura,
  // inclusive celular. Aqui a medida é do PAINEL, não da janela — ele vive
  // dentro do shell, com sidebar do lado, então `innerWidth` mentiria.
  //
  // Tailwind só gera classe que aparece literal no fonte, então nada de
  // `${x}:flex` montado por interpolação: ternário de classe INTEIRA, sempre.
  const cascaRef = useRef<HTMLDivElement>(null)
  const [larguraPainel, setLarguraPainel] = useState(1400)
  useEffect(() => {
    const el = cascaRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => setLarguraPainel(e.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  // Os cortes são pela largura do PAINEL, e foram calibrados pelo que sobra
  // para a conversa depois das laterais (200 + 260 = 460px):
  //   painel ≥ 1040  → três colunas, conversa com 580px ou mais
  //   780 a 1040     → a lista vira gaveta, conversa com ~520px
  //   abaixo de 780  → só a conversa; lista e ficha viram gaveta
  // Num notebook de 1366 o painel tem ~1086px, então ele continua com as três
  // colunas — cortar ali seria esconder a lista de quem tem espaço para ela.
  const estreito = larguraPainel < 780
  const medio = larguraPainel < 1040
  const [gaveta, setGaveta] = useState<'lista' | 'ficha' | null>(null)
  // Trocar de conversa fecha a gaveta: em tela estreita ela cobre o que a
  // pessoa acabou de escolher.
  useEffect(() => { if (medio || estreito) setGaveta(null) }, [atual?.id, medio, estreito])
  // Esc fecha a gaveta. Painel que cobre a tela e só fecha clicando exatamente
  // fora é armadilha em tela pequena, onde "fora" é uma faixa de 60px.
  useEffect(() => {
    if (!gaveta) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setGaveta(null) }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [gaveta])
  const [arrastando, setArrastando] = useState(false)
  const [focado, setFocado] = useState(false)
  // Mesmos três estados do compositor do painel Dados: gravando → transcrevendo
  // → parado. O botão de ação muda de cara conforme eles, em vez de haver quatro
  // botões disputando o canto.
  const [recState, setRecState] = useState<'idle' | 'recording' | 'transcribing'>('idle')
  const [recSecs, setRecSecs] = useState(0)
  const [recErro, setRecErro] = useState<string | null>(null)

  const fimRef = useRef<HTMLDivElement>(null)
  const fioRef = useRef<HTMLDivElement>(null)
  const campoRef = useRef<HTMLTextAreaElement>(null)
  const arquivoRef = useRef<HTMLInputElement>(null)
  const gravadorRef = useRef<MediaRecorder | null>(null)
  const pedacosRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const enviaRef = useRef<(t?: string) => void>(() => {})
  // Qual conversa está aberta AGORA. O turno leva de 1 a 10 minutos; se ela
  // trocar de campanha no meio, o que chega pelo fio é de outra conversa e não
  // pode escrever na tela — nem reabrir a errada.
  const atualIdRef = useRef<string | null>(null)
  useEffect(() => { atualIdRef.current = atual?.id ?? null }, [atual?.id])

  const enviando = etapa !== null

  const carregaLista = useCallback(async (selecionar?: string) => {
    const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_CHATS)
    const d = await r.json()
    if (!d.success) throw new Error(d.error || 'falha ao listar')
    setDesenvolvimento(d.desenvolvimento || [])
    setCompletas(d.completas || [])
    return [...(d.desenvolvimento || []), ...(d.completas || [])].find(
      (c: Chat) => c.id === selecionar,
    )
  }, [authenticatedFetch])

  const abre = useCallback(async (id: string) => {
    setErro(null)
    const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_CHAT(id))
    const d = await r.json()
    if (!d.success) { setErro(d.error || 'falha ao abrir'); return }
    setAtual(d.chat)
    setTurnos(d.turnos || [])
    // A conta é de UMA campanha. Deixar a da anterior na tela mostraria "58
    // pessoas" numa conversa que tem outra lista.
    setDisparo(null); setSemTesteOk(false)
    setTimeout(() => campoRef.current?.focus({ preventScroll: true }), 0)
  }, [authenticatedFetch])

  useEffect(() => {
    (async () => {
      try { await carregaLista() } catch (e) { setErro((e as Error).message) }
      finally { setCarregando(false) }
    })()
  }, [carregaLista])

  // ── A rolagem é DELA ──────────────────────────────────────────────────────
  //
  // Este efeito dependia de `etapa`, que muda a cada evento do fio (pensando,
  // lendo a lista, escrevendo…). Num turno de dez minutos — e há turnos de dez
  // minutos aqui — ele disparava dezenas de `scrollIntoView` e cada um cancelava
  // a rolagem que a pessoa estava fazendo. Era literalmente impossível voltar
  // para ler a mensagem anterior enquanto o agente respondia.
  //
  // Agora: só desce sozinho quem JÁ ESTAVA no fim. Quem subiu para ler continua
  // onde está e recebe um aviso de que chegou coisa nova.
  const PERTO_DO_FIM = 120
  const noFim = () => {
    const el = fioRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight <= PERTO_DO_FIM
  }
  const [temNovas, setTemNovas] = useState(false)
  // ── A caixa cresce com o que ela escreve ──────────────────────────────────
  //
  // Era `rows={2}` fixo. Quem escreve o texto de uma campanha escreve cinco,
  // seis linhas — e via duas, com o resto rolando numa caixa que não tem barra
  // de rolagem visível em lugar nenhum deste app. Some com o texto sem sumir de
  // verdade: a pessoa não sabe se escreveu ou se perdeu.
  const TETO_CAIXA = 176 // ~8 linhas; passou disso, a caixa rola por dentro
  // O PISO existe porque o clipe e o botão de enviar moram nos cantos de baixo
  // da caixa, em `absolute`: deixar a altura cair para uma linha desencaixa os
  // dois do texto. 64px = as duas linhas que o `rows={2}` dava antes.
  const PISO_CAIXA = 64
  useEffect(() => {
    const el = campoRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(Math.max(el.scrollHeight, PISO_CAIXA), TETO_CAIXA)}px`
  }, [entrada])

  // Quanto tempo esta resposta já está levando. Zera a cada turno novo.
  const [espera, setEspera] = useState(0)
  useEffect(() => {
    if (etapa === null) { setEspera(0); return }
    const id = setInterval(() => setEspera(s => s + 1), 1000)
    return () => clearInterval(id)
  }, [etapa === null])
  const desce = useCallback((suave = true) => {
    const el = fioRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: suave ? 'smooth' : 'auto' })
    setTemNovas(false)
  }, [])

  // Primeira carga e troca de conversa: vai direto ao fim, sem animação — a
  // animação a partir do topo faz a conversa inteira passar voando pelos olhos.
  useEffect(() => { desce(false) }, [atual?.id, desce])

  useEffect(() => {
    if (!turnos.length) return
    if (noFim()) desce(true)
    else setTemNovas(true)
    // `etapa` FICA DE FORA de propósito: ver o comentário acima.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnos])

  // Trava de clique repetido. Em 01/09 nasceram DUAS conversas "Nova campanha"
  // no mesmo segundo, do mesmo usuário: o botão aceitava o segundo clique
  // enquanto o primeiro POST ainda estava no ar. Conversa fantasma na lista é
  // barata de criar e cara de limpar.
  const criaChat = async () => {
    if (criandoChat) return
    setCriandoChat(true)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_CHATS, { method: 'POST', body: JSON.stringify({}) })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'falha ao criar')
      await carregaLista()
      setAtual(d.chat)
      setTurnos([])
      setTimeout(() => campoRef.current?.focus({ preventScroll: true }), 0)
    } catch (e) { setErro((e as Error).message) }
    finally { setCriandoChat(false) }
  }

  const descarta = async (id: string) => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_CHAT(id), { method: 'DELETE' })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'falha ao descartar')
      if (atual?.id === id) { setAtual(null); setTurnos([]) }
      await carregaLista()
    } catch (e) { setErro((e as Error).message) }
  }

  const enviaAnexo = async (arquivo: File | null | undefined) => {
    if (!arquivo || !atual || atual.disparada || anexando) return
    setErro(null); setAnexando(true)
    try {
      const fd = new FormData()
      fd.append('arquivo', arquivo)
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_ANEXO(atual.id), { method: 'POST', body: fd, timeout: 120000 })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'falha ao anexar')
      setAtual(a => (a ? { ...a, anexos: d.anexos } : a))
      // O backend grava um turno de sistema; espelha aqui pra o anexo aparecer
      // na conversa sem precisar recarregar. O texto tem que ser o MESMO que ele
      // gravou, leitura da arte inclusive: espelhar por alto fazia o turno TROCAR
      // de conteudo ao recarregar a pagina, e escondia justo a prova de que a peca
      // foi olhada.
      setTurnos(t => [...t, {
        id: `anx_${Date.now()}`, role: 'system', quando: new Date().toISOString(),
        content: d.anexo.tipo === 'planilha'
          ? `Planilha anexada: ${d.anexo.nome} — ${d.anexo.linhas} linhas, ${d.anexo.total} telefones válidos.`
          : `Imagem anexada: ${d.anexo.nome}.` + (d.anexo.leitura ? `\n\nO que eu li nela:\n${d.anexo.leitura}` : ''),
        meta: { aviso: d.anexo.aviso || null },
      }])
      setTimeout(() => campoRef.current?.focus({ preventScroll: true }), 0)
    } catch (e) { setErro((e as Error).message) }
    finally { setAnexando(false); if (arquivoRef.current) arquivoRef.current.value = '' }
  }

  // Áudio reusa a MESMA rota do painel Dados (/api/whisper/transcribe, Whisper
  // self-hosted). Vale pro microfone e pro arquivo de áudio arrastado: os dois
  // viram texto e entram como mensagem, não como anexo — o que a pessoa falou é
  // a mensagem dela.
  const transcreveEEnvia = useCallback(async (arquivo: File) => {
    setRecState('transcribing'); setRecErro(null)
    try {
      const fd = new FormData()
      fd.append('audio', arquivo)
      fd.append('language', 'pt')
      const r = await authenticatedFetch(API_ENDPOINTS.WHISPER_TRANSCRIBE, { method: 'POST', body: fd, timeout: 180000 })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      const texto = String(d.text || '').replace(/\s+/g, ' ').trim()
      if (!texto) { setRecErro('Não entendi o áudio — tente falar de novo.'); return }
      setEntrada(texto)
      enviaRef.current(texto)
    } catch (e) {
      setRecErro('Falha na transcrição: ' + (e as Error).message)
    } finally {
      setRecState('idle')
    }
  }, [authenticatedFetch])

  const gravar = useCallback(async () => {
    if (recState !== 'idle' || !atual || atual.disparada) return
    setRecErro(null)
    if (!navigator.mediaDevices?.getUserMedia) {
      setRecErro('Microfone exige HTTPS — abra por crm.twms.com.br.')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      pedacosRef.current = []
      mr.ondataavailable = e => { if (e.data.size) pedacosRef.current.push(e.data) }
      mr.onstop = () => {
        const tipo = mr.mimeType || 'audio/webm'
        const ext = tipo.includes('ogg') ? 'ogg' : tipo.includes('mp4') ? 'mp4' : 'webm'
        stream.getTracks().forEach(t => t.stop())
        transcreveEEnvia(new File([new Blob(pedacosRef.current, { type: tipo })], `campanha.${ext}`, { type: tipo }))
      }
      mr.start()
      gravadorRef.current = mr
      setRecState('recording'); setRecSecs(0)
      timerRef.current = setInterval(() => setRecSecs(x => x + 1), 1000)
    } catch (e) {
      setRecErro('Microfone indisponível: ' + (e as Error).message)
    }
  }, [recState, atual, transcreveEEnvia])

  const pararGravacao = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    gravadorRef.current?.stop()
  }, [])

  // solta microfone e timer se o painel desmontar no meio da gravação
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current)
    try { gravadorRef.current?.stream?.getTracks().forEach(t => t.stop()) } catch { /* */ }
  }, [])

  // Uma porta só pra tudo que cai na tela: clipe, colar (Ctrl+V) e arrastar.
  // Áudio vira texto; o resto vira anexo. Quem usa não precisa saber a diferença.
  const recebeArquivo = (arquivo: File | null | undefined) => {
    if (!arquivo) return
    if (arquivo.type.startsWith('audio/')) { transcreveEEnvia(arquivo); return }
    enviaAnexo(arquivo)
  }

  // Baixar a lista inteira. A conversa só mostra 3 linhas de amostra, e amostra
  // sozinha vira desconfiança — quem vai disparar precisa poder abrir os 43 no
  // Excel e ver, um por um, quem recebe e quem ficou de fora e por quê.
  const baixaLista = async (anexo: Anexo) => {
    if (!atual) return
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_ANEXO_CSV(atual.id, anexo.id), { timeout: 120000 })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const url = URL.createObjectURL(await r.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = `${(anexo.nome || 'lista').replace(/[^\w.-]+/g, '_').slice(0, 60)}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch (e) { setErro('Não consegui baixar a lista: ' + (e as Error).message) }
  }

  const removeAnexo = async (anexoId: string) => {
    if (!atual) return
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_ANEXO_ITEM(atual.id, anexoId), { method: 'DELETE' })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'falha ao remover')
      setAtual(a => (a ? { ...a, anexos: d.anexos, ficha: d.ficha, completa: !!d.completa, falta: d.falta || [] } : a))
    } catch (e) { setErro((e as Error).message) }
  }

  // Cadastrar de verdade na Meta. É a única ação desta tela que sai do CRM, e só
  // sai por aqui — clique de gente, nunca decisão do agente.
  const confirmaTemplate = async () => {
    if (!atual?.templateProposto || tplOcupado) return
    setErro(null); setTplOcupado('confirmando')
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_TEMPLATE_CONFIRMAR(atual.id), { method: 'POST', timeout: 180000 })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'a Meta não aceitou')
      setAtual(a => a ? { ...a, templateProposto: null, templatesCriados: d.templatesCriados as TemplateCriado[] } : a)
      setTurnos(t => [...t, {
        id: `tpl_${Date.now()}`, role: 'system', quando: new Date().toISOString(),
        content: `Template "${d.template.nome}" enviado para a Meta. Agora é esperar a análise.`,
      }])
    } catch (e) { setErro((e as Error).message) }
    finally { setTplOcupado(null) }
  }

  const descartaTemplate = async () => {
    if (!atual?.templateProposto || tplOcupado) return
    try {
      await authenticatedFetch(API_ENDPOINTS.AGENTE_TEMPLATE(atual.id), { method: 'DELETE' })
      setAtual(a => a ? { ...a, templateProposto: null } : a)
      setEntrada('Não gostei desse texto. Escreve outro assim: ')
      setTimeout(() => campoRef.current?.focus({ preventScroll: true }), 0)
    } catch (e) { setErro((e as Error).message) }
  }

  // "Já aprovou?" — a Meta não avisa a gente, então quem pergunta é quem espera.
  // O medidor é relido quando o público muda: é o alcance que decide se cabe.
  const alcance = atual?.ficha?.publico?.total ?? 0
  useEffect(() => {
    let vivo = true
    authenticatedFetch(`${API_ENDPOINTS.AGENTE_LIMITE}?alcance=${alcance}`)
      .then(r => r.json())
      .then(d => { if (vivo && d.success) setLimite(d as Limite) })
      .catch(() => { /* medidor é informação, não pode derrubar a tela */ })
    return () => { vivo = false }
  }, [alcance, atual?.id, authenticatedFetch])

  // "há 2 min" é o que responde a pergunta dela ("travou ou a Meta não respondeu?").
  // A hora cheia fica no title, pra quem quiser o dado exato.
  const desdeQuando = (iso?: string | null) => {
    if (!iso) return null
    const seg = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
    if (seg < 45) return 'agora mesmo'
    if (seg < 5400) return `há ${Math.round(seg / 60)} min`
    return `há ${Math.round(seg / 3600)} h`
  }

  const checaStatusTemplate = async () => {
    if (!atual || tplOcupado) return
    setErro(null); setTplOcupado('checando')
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_TEMPLATE_STATUS(atual.id), { method: 'POST', timeout: 60000 })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'não consegui perguntar à Meta')
      setAtual(a => a ? {
        ...a,
        templateChecadoEm: (d.templateChecadoEm as string) || a.templateChecadoEm,
        templateChecagemErro: (d.templateChecagemErro as string) ?? null,
        templatesCriados: (d.templatesCriados as TemplateCriado[]) || a.templatesCriados,
        ficha: (d.ficha as Ficha) || a.ficha,
        completa: d.completa ?? a.completa,
        falta: (d.falta as string[]) || a.falta,
      } : a)
    } catch (e) { setErro((e as Error).message) }
    finally { setTplOcupado(null) }
  }

  // Manda a mensagem para UM número, com o dado real do primeiro contato da
  // lista. É a última coisa entre ela e 84 mensagens pagas.
  const mandaTeste = async () => {
    if (!atual?.ficha?.teste?.numero || tplOcupado) return
    setErro(null); setTplOcupado('testando')
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_TESTE(atual.id), { method: 'POST', timeout: 120000 })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'não consegui mandar o teste')
      setAtual(a => a ? { ...a, ficha: (d.ficha as Ficha) || a.ficha, testes: d.testes } : a)
      setTurnos(t => [...t, {
        id: `tst_${Date.now()}`, role: 'system', quando: new Date().toISOString(),
        content: `Teste enviado para ${d.teste.comoFoiDito}. Olha no celular: é exatamente assim que vai chegar pra todo mundo.`,
      }])
    } catch (e) { setErro((e as Error).message) }
    finally { setTplOcupado(null) }
  }

  // ── O disparo, em dois cliques ──
  // O primeiro só CONTA: quantas pessoas recebem, quem ficou de fora e por quê.
  // Um botão só, que já manda, transformaria um clique errado em N mensagens
  // pagas — e não há desfazer depois que a Meta aceita a primeira.
  const veQuemRecebe = async () => {
    if (!atual || disparoOcupado) return
    setErro(null); setDisparoOcupado('contando')
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_DISPARO(atual.id), { timeout: 60000 })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'não consegui montar a conta do disparo')
      setDisparo(d.disparo as Disparo)
      setSemTesteOk(false)
    } catch (e) { setErro((e as Error).message) }
    finally { setDisparoOcupado(null) }
  }

  // O segundo clique. Daqui saem mensagens pagas para gente de verdade.
  const disparaAgora = async () => {
    if (!atual || !disparo || disparoOcupado) return
    setErro(null); setDisparoOcupado('disparando')
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_DISPARAR(atual.id), {
        method: 'POST', timeout: 120000,
        body: JSON.stringify({ confirmo: true, semTeste: semTesteOk }),
      })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'não consegui disparar')
      setDisparo(null)
      setTurnos(t => [...t, {
        id: `dsp_${Date.now()}`, role: 'system', quando: new Date().toISOString(),
        content: d.fechada
          ? `Campanha disparada para ${d.disparo.quantidade} ${d.disparo.quantidade === 1 ? 'pessoa' : 'pessoas'}. As mensagens saem uma a uma; os números de entrega e leitura aparecem em Marketing → Meta ADS.`
          : `Lote ${d.disparo.lote} disparado para ${d.disparo.quantidade} pessoas. Ainda ${d.disparo.esperam === 1 ? 'falta 1 pessoa' : `faltam ${d.disparo.esperam} pessoas`} — o cartão volta quando o limite do dia virar, e ninguém recebe duas vezes.`,
      }])
      // Recarrega do servidor: a conversa pode ter FECHADO (vai para "Já
      // disparadas") ou continuar aberta esperando o próximo lote. Quem decide
      // isso é o servidor, então a tela relê em vez de adivinhar.
      await carregaLista()
      await abre(atual.id)
    } catch (e) { setErro((e as Error).message) }
    finally { setDisparoOcupado(null) }
  }

  const envia = async (override?: string) => {
    const texto = (override ?? entrada).trim()
    if (!texto || !atual || enviando) return
    setEntrada('')
    setErro(null)
    setOpcoes([])
    setEtapa('Pensando…')
    // Otimista: a fala já foi gravada no servidor antes do modelo ser chamado,
    // então mostrar na hora não mente sobre o estado.
    const provisorio: Turno = { id: `tmp_${Date.now()}`, role: 'user', content: texto, quando: new Date().toISOString() }
    setTurnos(t => [...t, provisorio])

    // A conversa DESTE turno. Tudo abaixo se refere a ela, não à que estiver
    // aberta quando a resposta voltar.
    const alvo = atual
    const daConversa = () => atualIdRef.current === alvo.id

    try {
      const r = await authenticatedFetch(API_ENDPOINTS.AGENTE_TURNO(alvo.id), {
        method: 'POST',
        headers: { Accept: 'text/event-stream' },
        body: JSON.stringify({ texto }),
        timeout: 300000,
      })
      if (!r.ok || !r.body) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.error || `HTTP ${r.status}`)
      }
      await consumeSse(r.body, {
        // Trocou de conversa? Para de consumir. Sem isto o consumo seguia vivo
        // e o `onEvent` escrevia na conversa que ela estava lendo.
        aindaValido: daConversa,
        onEvent: (evento, carga) => {
          if (!daConversa()) return
          if (evento === 'status') setEtapa(String(carga.rotulo || 'Pensando…'))
          else if (evento === 'leitura') setEtapa(`Lendo ${String(carga.o_que).replace(/_/g, ' ')}…`)
          else if (evento === 'ficha') {
            setAtual(a => a ? { ...a, ficha: carga.ficha as Ficha, completa: !!carga.completa, falta: (carga.falta as string[]) || [] } : a)
          } else if (evento === 'template') {
            setAtual(a => a ? { ...a, templateProposto: carga.template as TemplateProposto } : a)
          } else if (evento === 'done') {
            // O agente pode ter puxado uma lista do Draco no meio do turno: isso
            // cria anexo E um turno de sistema. Se a contagem mudou, releio a
            // conversa inteira em vez de tentar remontar o que aconteceu.
            const anexosDepois = (carga.anexos as Anexo[]) || []
            const anexosAntes = alvo.anexos?.length ?? 0
            setAtual(a => a ? {
              ...a,
              ficha: carga.ficha as Ficha,
              completa: !!carga.completa,
              falta: (carga.falta as string[]) || [],
              titulo: (carga.titulo as string) || a.titulo,
              anexos: anexosDepois.length ? anexosDepois : a.anexos,
              templateProposto: (carga.templateProposto as TemplateProposto) ?? null,
              templatesCriados: (carga.templatesCriados as TemplateCriado[]) || a.templatesCriados,
            } : a)
            setOpcoes((carga.opcoes as string[]) || [])
            if (anexosDepois.length !== anexosAntes) setTimeout(() => { if (daConversa()) abre(alvo.id) }, 400)
            setTurnos(t => [...t, {
              id: `res_${Date.now()}`, role: 'assistant', content: String(carga.fala || ''),
              quando: new Date().toISOString(),
              meta: {
                recusados: (carga.recusados as Array<{ campo: string; motivo: string }>) || [],
                leituras: (carga.leituras as string[]) || [],
              },
            }])
          } else if (evento === 'error') {
            setErro(String(carga.erro || 'erro no turno'))
          }
        },
      })
      await carregaLista()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setEtapa(null)
      setTimeout(() => campoRef.current?.focus({ preventScroll: true }), 0)
    }
  }

  // `transcreveEEnvia` é memoizado (não pode remontar o MediaRecorder a cada
  // tecla), mas precisa chamar o `envia` deste render — daí o ref.
  useEffect(() => { enviaRef.current = envia })

  // Enquanto houver mensagem em análise, pergunta à Meta sozinho. A Meta não
  // avisa ninguém, e mandar a pessoa clicar "já saiu?" de minuto em minuto é
  // pedir que ela vigie o nosso trabalho.
  //
  // 90s e só com a aba à vista: a rota fura o cache de 5 min de propósito, então
  // um intervalo curto (ou uma aba esquecida aberta a noite toda) viraria carga
  // no Graph, que tem limite por hora — foi isso que derrubou o Ao Vivo em 15/07.
  const statusRef = useRef<() => void>(() => {})
  useEffect(() => { statusRef.current = checaStatusTemplate })
  const esperando = !!atual?.templatesCriados?.some(t => t.status !== 'APPROVED' && t.status !== 'REJECTED')
  useEffect(() => {
    if (!esperando) return
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') statusRef.current()
    }, 90000)
    return () => clearInterval(id)
  }, [esperando])


  // Cada linha da lista diz DUAS coisas: o nome da campanha e o quanto ela
  // andou. Antes só existia um ponto de 6px de cor — a lista inteira era uma
  // pilha de frases cinzas do mesmo tamanho, e descobrir qual campanha estava
  // pronta exigia abrir uma por uma.
  const listaDeChats = (titulo: string, itens: Chat[], vazio: string) => (
    <div className="mb-4">
      <div className="flex items-center gap-1.5 px-2 pb-1.5">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{titulo}</span>
        {itens.length > 0 && (
          <span className="neu-chip rounded-full px-1.5 text-[10.5px] font-semibold leading-[15px]">{itens.length}</span>
        )}
      </div>
      {itens.length === 0
        ? <div className="px-2 py-1 text-[11px] text-muted-foreground">{vazio}</div>
        : itens.map(c => {
          const estado = c.disparada ? 'já foi para as clientes'
            : c.completa ? 'pronta pra disparar'
            : c.falta.length === 1 ? 'falta 1 item'
            : `faltam ${c.falta.length} itens`
          const cor = c.disparada ? 'bg-emerald-500' : c.completa ? 'bg-amber-500' : 'bg-muted-foreground/45'
          return (
            <div
              key={c.id}
              onClick={() => abre(c.id)}
              className={`group mb-1 flex items-center gap-2.5 px-2.5 py-2 rounded-xl cursor-pointer transition-all ${
                atual?.id === c.id ? 'neu-inset text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className={`neu-dot w-2 h-2 rounded-full shrink-0 ${cor}`} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-medium leading-tight" title={c.titulo}>{c.titulo}</span>
                <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">{estado}</span>
              </span>
              {!c.disparada && (
                <button
                  onClick={e => { e.stopPropagation(); descarta(c.id) }}
                  title="Descartar esta conversa"
                  className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )
        })}
    </div>
  )
  // ── A ficha vira medidor ────────────────────────────────────────────────
  // Os cinco campos que a ficha guarda, contados aqui só pra virar KPI no
  // título da coluna. O denominador é honesto: o que já está de pé MAIS o que o
  // servidor ainda cobra em `falta` — nunca um "5" fixo, porque nem toda
  // campanha precisa de arte ou de dado por pessoa.
  const ficha = atual?.ficha
  const temVariaveis = !!ficha?.variaveis && Object.keys(ficha.variaveis).length > 0
  const feitos = ficha
    ? [!!ficha.objetivo, !!ficha.publico, !!ficha.template, !!ficha.imagem, temVariaveis].filter(Boolean).length
    : 0
  const totalFicha = feitos + (atual?.falta.length ?? 0)
  const pct = totalFicha ? Math.round((feitos / totalFicha) * 100) : 0

  // Linha da ficha em DOIS pesos. Cheia: ícone no verde da marca, rótulo miúdo
  // e o valor grande, porque o valor é o que se lê. Vazia: uma linha baixa e
  // calada com um travessão. Antes as duas tinham o mesmo tamanho e a coluna
  // virava cinco caixas iguais dizendo "ainda não" — o que falta gritava tanto
  // quanto o que já existe, e nada tinha ritmo.
  const linhaFicha = (Icone: IconType, rotulo: string, valor: ReactNode, preenchido: boolean) =>
    preenchido ? (
      <div className="flex gap-2.5 px-3.5 py-2.5">
        <Icone className="mt-[3px] h-3.5 w-3.5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{rotulo}</div>
          <div className="mt-1 text-[12.5px] leading-snug text-foreground">{valor}</div>
        </div>
      </div>
    ) : (
      <div className="flex items-center gap-2.5 px-3.5 py-2 text-muted-foreground">
        <Icone className="h-3.5 w-3.5 shrink-0 opacity-45" />
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em]">{rotulo}</span>
        <span className="ml-auto text-[11px] opacity-50">—</span>
      </div>
    )

  const mapaDaFicha = (
    <>
      {linhaFicha(FiTarget, 'Objetivo', ficha?.objetivo, !!ficha?.objetivo)}
      {linhaFicha(FiUsers, 'Quem vai receber',
        ficha?.publico && (
          <>
            {ficha.publico.descricao}
            {ficha.publico.total != null && (
              <span className="ml-1 font-medium text-primary">· {ficha.publico.total.toLocaleString('pt-BR')} contatos</span>
            )}
            {ficha.publico.fonte && (
              <span className="mt-1 block truncate text-[10.5px] text-muted-foreground">{ficha.publico.fonte}</span>
            )}
          </>
        ), !!ficha?.publico)}
      {linhaFicha(FiMessageSquare, 'A mensagem',
        ficha?.template && (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="break-all font-mono text-[11px] leading-snug">{ficha.template.nome}</span>
            {ficha.template.status && (
              <span className={`neu-tag rounded-md px-1.5 py-[1px] text-[10.5px] font-semibold uppercase tracking-wide ${COR_STATUS[ficha.template.status] || 'text-muted-foreground'}`}>
                {ROTULO_STATUS[ficha.template.status] || ficha.template.status}
              </span>
            )}
          </span>
        ), !!ficha?.template)}
      {linhaFicha(FiImage, 'A arte',
        ficha?.imagem && (
          <div className="flex items-center gap-2">
            {/^video\//.test(ficha.imagem.tipo || '')
              ? <video src={ficha.imagem.url} muted playsInline preload="metadata" className="h-9 w-9 shrink-0 rounded-lg object-cover neu-avatar" />
              : <img src={ficha.imagem.url} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover neu-avatar" />}
            <span className="truncate">{ficha.imagem.nome}</span>
          </div>
        ), !!ficha?.imagem)}
      {linhaFicha(FiTag, 'Muda em cada envio',
        ficha?.variaveis && (
          <div className="space-y-1">
            {Object.entries(ficha.variaveis).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="shrink-0 rounded-md bg-primary/12 px-1.5 py-[1px] font-mono text-[10px] text-primary">{`{{${k}}}`}</span>
                <span className="truncate">{v.tipo === 'coluna' ? `coluna ${v.valor}` : v.valor}</span>
              </div>
            ))}
          </div>
        ), temVariaveis)}
    </>
  )

  const prop = atual?.templateProposto || null

  // ── A proposta pode ser guardada ──────────────────────────────────────────
  //
  // A prévia da mensagem é o cartão mais alto da tela e fica aberta o tempo
  // todo enquanto a Meta não aprova — mas nem sempre é sobre ela que se está
  // falando. Guardada, ela vira uma linha na Ficha (e um chip aqui embaixo),
  // e a conversa fica com a altura inteira. Abre de novo com um clique.
  //
  // Volta a abrir sozinha quando o agente propõe OUTRA mensagem: aí é assunto
  // novo, e esconder o que acabou de mudar seria esconder justamente o que ela
  // precisa conferir.
  const [propGuardada, setPropGuardada] = useState(false)
  const assinaturaProp = prop ? `${prop.nome}|${prop.propostoEm}` : null
  const propAnteriorRef = useRef<string | null>(null)
  useEffect(() => {
    if (assinaturaProp !== propAnteriorRef.current) {
      propAnteriorRef.current = assinaturaProp
      setPropGuardada(false)
    }
  }, [assinaturaProp])

  // ── Os cartões de sistema, agrupados e recolhidos ─────────────────────────
  const LIMITE_CARTAO = 240
  const [aberto, setAberto] = useState<Set<string>>(new Set())
  const alterna = (id: string) => setAberto(s => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })
  // Tira só a marcação que o cartão não renderiza (ele é texto puro, não
  // markdown): `**negrito**` aparecia com os asteriscos na tela.
  const semMarcacao = (txt: string) => txt.replace(/\*\*(.+?)\*\*/g, '$1').replace(/^#{1,4}\s+/gm, '')

  // Repetidos CONSECUTIVOS do mesmo assunto viram um só, com os anteriores
  // guardados. O assunto é a parte antes do primeiro ':' — é o que distingue
  // "Lista puxada do Draco" de "Imagem anexada".
  type TurnoNaTela = Turno & { antes?: Turno[] }
  const turnosNaTela: TurnoNaTela[] = []
  for (const t of turnos) {
    const ultimo = turnosNaTela[turnosNaTela.length - 1]
    const assunto = (x: Turno) => x.content.split(':')[0].trim()
    if (ultimo && ultimo.role === 'system' && t.role === 'system' && assunto(ultimo) === assunto(t)) {
      // O mais NOVO é o que vale (foi ele que o agente usou); os outros ficam
      // debaixo dele, na ordem em que aconteceram.
      turnosNaTela[turnosNaTela.length - 1] = { ...t, antes: [...(ultimo.antes || []), { ...ultimo, antes: undefined } as Turno] }
    } else {
      turnosNaTela.push(t)
    }
  }

  // ── UM PASSO POR VEZ ──────────────────────────────────────────────────────
  //
  // Estes cartões eram SEIS blocos empilhados entre a conversa e a caixa de
  // texto, todos `shrink-0` e sem teto de altura. Numa campanha madura — que é
  // o estado normal de quem trabalha aqui — eles somavam 888px numa janela de
  // 768: a conversa ficava com 32px e a caixa de texto saía da tela. Medido em
  // 04/09/2026, e é a queixa da Nathany.
  //
  // Agora só o gesto DA VEZ ocupa espaço. O resto vira chip na linha de resumo,
  // e o que já aconteceu está na conversa, onde aconteceu.
  //
  // A ordem é a do fluxo real: escrever → aprovar na Meta → esperar → testar →
  // disparar. O disparo é o último e NUNCA é escondido por outro cartão: foi
  // por não achar o botão que a Julia travou em 31/08.
  const passo: 'proposta' | 'aprovacao' | 'teste' | 'disparo' | null =
    !atual || atual.disparada ? null
      : prop && !propGuardada ? 'proposta'
      : esperando ? 'aprovacao'
      : atual.completa && !atual.ficha?.teste?.enviadoEm && atual.ficha?.teste?.numero ? 'teste'
      : atual.completa ? 'disparo'
      : null

  // O que a pessoa precisa ter à mão o tempo todo, sem custar altura: uma linha
  // só, que rola de lado se não couber. Cada chip responde uma pergunta que
  // antes ocupava um cartão inteiro.
  const chipsResumo: Array<{ txt: string; tom?: 'alerta' | 'ok'; abre?: () => void }> = []
  if (atual && !atual.disparada) {
    // A mensagem guardada primeiro: com a Ficha em gaveta (tela estreita) este
    // chip é o único caminho de volta até ela.
    if (prop && propGuardada) chipsResumo.push({ txt: '✏ mensagem escrita — abrir', abre: () => setPropGuardada(false) })
    const t = atual.ficha?.publico?.total
    if (t) chipsResumo.push({ txt: `${t} ${t === 1 ? 'contato' : 'contatos'}` })
    if (limite?.configurado) {
      if (limite.lotes) chipsResumo.push({ txt: `${limite.limite}/dia · ${limite.lotes.total} lotes`, tom: 'alerta' })
      else if (limite.restam !== null) chipsResumo.push({ txt: `restam ${limite.restam} hoje` })
    }
    const st = atual.ficha?.template?.status
    if (st) chipsResumo.push({ txt: `mensagem ${ROTULO_STATUS[st] || st.toLowerCase()}`, tom: st === 'APPROVED' ? 'ok' : undefined })
    if (atual.ficha?.teste?.enviadoEm) chipsResumo.push({ txt: 'teste enviado', tom: 'ok' })
    if (atual.disparos?.length) chipsResumo.push({ txt: `lote ${atual.disparos.length} enviado`, tom: 'ok' })
  }

  // {{1}} não é palavra: é o buraco onde entra o dado de cada pessoa. Mostrar o
  // buraco cru obrigava um parágrafo embaixo explicando o que ele significa —
  // aqui ele vira pastilha com o nome do que entra, e o parágrafo some.
  const corpoComPastilhas = (corpo: string) =>
    corpo.split(/(\{\{\d+\}\})/g).map((p, i) => {
      const m = p.match(/^\{\{(\d+)\}\}$/)
      if (!m) return <span key={i}>{p}</span>
      const mapeada = ficha?.variaveis?.[m[1]]
      return (
        <span key={i} className="rounded-md bg-black/[0.09] px-1 py-[1px] text-[11.5px] font-medium dark:bg-white/15">
          {mapeada ? (mapeada.tipo === 'coluna' ? mapeada.valor.toLowerCase() : mapeada.valor) : 'dado de cada pessoa'}
        </span>
      )
    })

  const agora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  // As quatro portas de entrada do compositor, ditas onde a pessoa está parada
  // e não num parágrafo corrido: quem lê pouco varre pastilha, não frase.
  const pistas = (
    <div className="mt-6 flex flex-wrap items-center justify-center gap-1.5">
      {/* A pastilha do microfone só entra para quem TEM microfone. Prometer "pode
          falar em vez de digitar" a quem não tem o botão é a mesma armadilha de
          sempre: a pessoa procura, não acha, e conclui que o sistema quebrou. */}
      {([...(podeFalar ? [[FiMic, 'pode falar em vez de digitar']] : []),
         [FiImage, 'cole a arte aqui'],
         [FiGrid, 'arraste a planilha'], [FiDatabase, 'ou peça a lista do Draco']] as [IconType, string][])
        .map(([Icone, txt]) => (
          <span key={txt} className="flex items-center gap-1.5 neu-chip rounded-full px-2.5 py-1 text-[11px]">
            <Icone className="w-3 h-3 shrink-0" /> {txt}
          </span>
        ))}
    </div>
  )

  return (
    <div ref={cascaRef} className="h-full flex min-h-0 text-foreground relative">
      {/* Véu da gaveta: sem ele, clicar fora não fecha e a pessoa fica presa
          numa coluna que cobre a conversa. */}
      {gaveta && (
        <div onClick={() => setGaveta(null)} className="absolute inset-0 z-20 bg-black/30" />
      )}
      {/* ── Coluna 1: as campanhas ──
          Fio de 1px não separa nada sobre a base neu: a divisa vira aresta
          (neu-ledge-r), que é o que o sistema tem pra "esta superfície termina
          aqui". Mesma troca na divisa da Ficha. */}
      <aside className={medio
        ? `absolute inset-y-0 left-0 z-30 w-[260px] bg-card border-r border-border flex flex-col min-h-0 shadow-2xl transition-transform ${gaveta === 'lista' ? 'translate-x-0' : '-translate-x-full'}`
        : 'w-[200px] shrink-0 border-r border-border neu-ledge-r flex flex-col min-h-0'}>
        <div className="p-3">
          <button
            onClick={criaChat}
            disabled={criandoChat}
            className="w-full flex items-center justify-center gap-1.5 px-3 h-9 rounded-xl bg-primary text-primary-foreground text-[12.5px] font-semibold neu-relief hover:brightness-110 disabled:opacity-60 disabled:cursor-wait transition-all"
          >
            {criandoChat ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />} Nova campanha
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3">
          {carregando
            ? <div className="px-2 text-[11px] text-muted-foreground">carregando…</div>
            : <>
              {listaDeChats('Em desenvolvimento', desenvolvimento, 'nenhuma por enquanto')}
              {listaDeChats('Já disparadas', completas, 'nenhuma ainda')}
            </>}
        </div>
      </aside>

      {/* ── Coluna 2: a conversa ── */}
      <section
        className="relative flex-1 min-w-0 flex flex-col min-h-0"
        onDragOver={e => { if (atual && !atual.disparada) { e.preventDefault(); setArrastando(true) } }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setArrastando(false) }}
        onDrop={e => { e.preventDefault(); setArrastando(false); recebeArquivo(e.dataTransfer.files?.[0]) }}
      >
        {/* Alvo do arrastar é a COLUNA inteira, não só a lista de mensagens:
            quem arrasta um arquivo mira no meio da tela, não numa faixa. */}
        {arrastando && (
          <div className="absolute inset-2 z-20 rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 text-[12.5px] font-medium text-primary">
              <FiPaperclip className="w-4 h-4" /> Solte aqui — planilha, imagem ou áudio
            </div>
          </div>
        )}
        <header className="px-4 py-3 border-b border-border neu-ledge-b flex items-center gap-2.5 shrink-0">
          {/* Em tela apertada as laterais viram gaveta, e a porta delas mora
              aqui: coluna que some sem porta é funcionalidade que sumiu. */}
          {medio ? (
            <button onClick={() => setGaveta(g => (g === 'lista' ? null : 'lista'))}
              title="As suas campanhas"
              className="w-7 h-7 rounded-lg neu-item flex items-center justify-center shrink-0">
              <FiMessageSquare className="w-3.5 h-3.5" />
            </button>
          ) : (
            <span className="w-7 h-7 rounded-full neu-avatar flex items-center justify-center shrink-0">
              <FiZap className="w-3.5 h-3.5 text-primary" />
            </span>
          )}
          <span className="truncate text-[14px] font-semibold" title={atual?.titulo}>{atual ? atual.titulo : 'Campanhas'}</span>
          {atual?.disparada && (
            <span className="ml-auto shrink-0 neu-tag rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">disparada</span>
          )}
          {estreito && atual && (
            <button onClick={() => setGaveta(g => (g === 'ficha' ? null : 'ficha'))}
              title="A ficha da campanha"
              className={`${atual.disparada ? '' : 'ml-auto'} shrink-0 h-7 px-2.5 rounded-lg neu-item text-[11px] flex items-center gap-1.5`}>
              <FiGrid className="w-3.5 h-3.5" /> Ficha
            </button>
          )}
        </header>

        {erro && (
          <div className="mx-4 mt-3 flex items-start gap-2 px-3 py-2 rounded-xl neu-tag text-red-600 dark:text-red-400 text-[11.5px]">
            <FiAlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="flex-1 text-foreground">{erro}</span>
            <button onClick={() => setErro(null)} className="opacity-60 hover:opacity-100">✕</button>
          </div>
        )}

        {/* O fio.
            `min-h-0` e não um piso em %: piso aqui NÃO protege a conversa, faz o
            contrário — como os cartões abaixo são `shrink-0`, forçar altura no
            fio empurra a caixa de texto para fora da janela (medido: base em
            956px numa janela de 900px). Quem protege a conversa é o teto do
            cartão de ação e o fato de só existir UM por vez.
            `overscroll-contain` impede que o fim da conversa role a página atrás. */}
        {/* `neu-div-b` fecha a área que rola com uma aresta. Sem ela, a
            conversa e os cartões fixos abaixo pareciam uma superfície só — e
            quem chega no fim do fio acha que a rolagem travou, em vez de ver
            que ali acabou o que rola. É o separador do vocabulário da casa
            (sulco de 2px), e vale em elemento chapado como este. */}
        <div ref={fioRef} onScroll={() => { if (noFim()) setTemNovas(false) }}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 fio-rolagem border-b border-border neu-div-b">
          <div className="max-w-3xl mx-auto space-y-3 min-h-full flex flex-col">
          {/* ── Nada aberto ──
              Antes esta coluna era um campo cinza com um parágrafo no meio e uma
              caixa de texto DESLIGADA embaixo dizendo "crie uma campanha" — a
              instrução estava aqui e o botão a 900px de distância. Agora o botão
              está onde a frase está. */}
          {!atual && (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <span className="w-14 h-14 rounded-full neu-avatar flex items-center justify-center">
                <FiZap className="w-6 h-6 text-primary" />
              </span>
              <h2 className="mt-5 text-[18px] font-semibold leading-tight">Cada conversa aqui é uma campanha.</h2>
              <p className="mt-2 max-w-[380px] text-[12.5px] leading-relaxed text-muted-foreground">
                Conte o que você quer dizer e para quem. A ficha ao lado vai marcando o que já tem
                e o que ainda falta — você não precisa lembrar de nada.
              </p>
              <button
                onClick={criaChat}
                className="mt-6 flex items-center gap-2 h-10 px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-semibold neu-relief hover:brightness-110 transition-all"
              >
                <FiPlus className="w-4 h-4" /> Começar uma campanha
              </button>
              {pistas}
            </div>
          )}

          {/* ── Campanha aberta e nada dito ainda ──
              Era a segunda tela morta do painel: coluna cinza de 900px com um
              cursor piscando no rodapé. A primeira frase é a mais cara de todas
              — estes três começos MANDAM o texto escrito neles, do mesmo jeito
              que as respostas prontas: é a fala dela, não atalho de comando. */}
          {atual && !atual.disparada && turnos.length === 0 && !etapa && (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <h2 className="text-[16px] font-semibold">Do que é a sua campanha?</h2>
              <p className="mt-1.5 max-w-[360px] text-[12.5px] leading-relaxed text-muted-foreground">
                Conte com as suas palavras — ou comece por um destes:
              </p>
              <div className="mt-4 flex w-full max-w-[400px] flex-col gap-1.5">
                {[
                  'Quero avisar as minhas clientes de uma promoção.',
                  'Quero convidar as recrutas para um evento.',
                  'Quero dar as boas-vindas para quem entrou agora.',
                ].map(o => (
                  <button
                    key={o}
                    onClick={() => envia(o)}
                    className="neu-item rounded-xl px-3.5 py-2.5 text-left text-[12.5px] transition-all"
                  >
                    {o}
                  </button>
                ))}
              </div>
              {pistas}
            </div>
          )}

          {turnosNaTela.map(t => t.role === 'system' ? (
            // Anexo é parte da conversa, não decoração: entra na linha do tempo,
            // no lugar onde foi jogado.
            //
            // Duas coisas mudaram em 04/09/2026, olhando uma conversa real:
            //
            // 1) Pedir a lista de novo (trocar um filtro) grava um cartão a
            //    cada vez — havia QUATRO "Lista puxada do Draco" quase iguais
            //    empilhados, 910/908/910. As repetidas seguidas agora contam
            //    numa linha e abrem quando ela quiser: a prova continua ali,
            //    sem ocupar a conversa quatro vezes.
            // 2) O cartão da leitura da arte tem 2.043 caracteres e vinha com
            //    os `**` do negrito na cara. Recolhe em 3 linhas e limpa a
            //    marcação — este cartão é recado, não documento.
            <div key={t.id} className="flex justify-center">
              <div className="max-w-[90%] rounded-xl neu-chip px-2.5 py-1.5 text-[11px] flex items-start gap-1.5">
                {t.content.startsWith('Lista puxada') ? <FiDatabase className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
                  : t.content.startsWith('Planilha') ? <FiGrid className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  : <FiImage className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                <span className="min-w-0">
                  {t.antes && t.antes.length > 0 && (
                    <button onClick={() => alterna(t.id)}
                      className="mb-1 block text-[10px] font-medium text-primary hover:underline">
                      {aberto.has(t.id)
                        ? 'esconder as versões anteriores'
                        : `refeita ${t.antes.length + 1}× — ver as ${t.antes.length} anteriores`}
                    </button>
                  )}
                  {aberto.has(t.id) && t.antes?.map(v => (
                    <span key={v.id} className="block mb-1 opacity-60 whitespace-pre-wrap">{semMarcacao(v.content)}</span>
                  ))}
                  <span className="whitespace-pre-wrap block">
                    {aberto.has(t.id) || t.content.length <= LIMITE_CARTAO
                      ? semMarcacao(t.content)
                      : `${semMarcacao(t.content).slice(0, LIMITE_CARTAO).trimEnd()}…`}
                  </span>
                  {t.content.length > LIMITE_CARTAO && !t.antes?.length && (
                    <button onClick={() => alterna(t.id)}
                      className="mt-0.5 text-[10px] font-medium text-primary hover:underline">
                      {aberto.has(t.id) ? 'ver menos' : 'ver tudo'}
                    </button>
                  )}
                  {t.meta?.aviso && <span className="block mt-0.5 text-amber-600 dark:text-amber-400">{t.meta.aviso}</span>}
                </span>
              </div>
            </div>
          ) : (
            <div key={t.id} className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[min(84%,58ch)] rounded-2xl px-3.5 py-2.5 text-[12.5px] leading-relaxed ${
                t.role === 'user'
                  ? 'rounded-tr-md bg-card neu-bubble-out border border-border'
                  : 'rounded-tl-md neu-bubble-in'
              }`}>
                {t.role === 'assistant'
                  ? <div className="chat-markdown max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{t.content}</ReactMarkdown></div>
                  : <div className="whitespace-pre-wrap">{t.content}</div>}
                {!!t.meta?.leituras?.length && (
                  <div className="mt-2 pt-1.5 border-t border-border/50 text-[10px] text-muted-foreground">
                    consultou: {t.meta.leituras.join(', ').replace(/_/g, ' ')}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* A espera com cara de espera. Um turno aqui leva de 1 a 10 minutos
              (o mais longo medido: 9min55s), e três palavras paradas na tela
              fazem qualquer pessoa achar que travou. O relógio anda, então dá
              pra ver que está vivo — e a frase diz o que é normal. */}
          {etapa && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-tl-md neu-bubble-in px-3.5 py-2.5 text-[11.5px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <FiLoader className="w-3 h-3 animate-spin" /> {etapa}
                  <span className="tabular-nums opacity-70">{espera >= 60 ? `${Math.floor(espera / 60)}min${String(espera % 60).padStart(2, '0')}` : `${espera}s`}</span>
                </span>
                {espera >= 25 && (
                  <span className="block mt-1 text-[10.5px] opacity-70">
                    {espera >= 180
                      ? 'Ainda trabalhando — listas grandes levam alguns minutos. Pode deixar aberto.'
                      : 'Costuma levar de 1 a 3 minutos quando ele lê a sua lista.'}
                  </span>
                )}
              </div>
            </div>
          )}
          <div ref={fimRef} />
          </div>
        </div>

        {/* Chegou mensagem enquanto ela lia lá em cima. O aviso é o oposto de
            arrastar a tela: ela decide quando descer. */}
        {temNovas && (
          <div className="relative">
            <button onClick={() => desce(true)}
              className="absolute left-1/2 -translate-x-1/2 -top-9 z-10 h-7 px-3 rounded-full text-[11px] font-medium bg-primary text-primary-foreground neu-send shadow-lg flex items-center gap-1.5">
              ↓ mensagem nova
            </button>
          </div>
        )}

        {/* ── A linha de resumo ──
            Era um cartão inteiro por informação (o limite do dia tinha barra,
            três frases e um rodapé). Viraram chips numa linha só: o que é
            CONTEXTO não pode custar a altura da conversa. O detalhe do limite —
            quantos saem hoje, quantos lotes, quando volta — está por extenso no
            cartão de disparo, que é onde ela decide. */}
        {!!chipsResumo.length && (
          <div className="px-4 pb-1.5 shrink-0">
            <div className="max-w-3xl mx-auto flex items-center gap-1.5 overflow-x-auto fio-chips">
              {chipsResumo.map((c, i) => {
                const cor = c.abre ? 'text-primary font-medium'
                  : c.tom === 'alerta' ? 'text-amber-700 dark:text-amber-400'
                  : c.tom === 'ok' ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-muted-foreground'
                const classe = `shrink-0 rounded-full px-2.5 py-1 text-[11px] neu-chip ${cor}`
                return c.abre
                  ? <button key={i} onClick={c.abre} className={`${classe} hover:brightness-105 transition-all`}>{c.txt}</button>
                  : <span key={i} className={classe}>{c.txt}</span>
              })}
            </div>
          </div>
        )}

        {/* ── Respostas prontas ──
            Uma caixa de texto vazia trava quem não sabe o que responder. Estes
            botões mandam exatamente o texto que está escrito neles — não é
            atalho de comando, é a fala dela, então a conversa segue igual.
            Somem quando ela responde e quando a campanha já foi disparada. */}
        {!!opcoes.length && !enviando && atual && !atual.disparada && (
          <div className="px-4 pb-1.5 shrink-0">
            <div className="max-w-3xl mx-auto flex flex-wrap gap-1.5">
              {opcoes.map((o, i) => (
                <button
                  key={i}
                  onClick={() => { setOpcoes([]); envia(o) }}
                  className="neu-tag rounded-full px-3 py-1.5 text-[11.5px] font-medium text-primary hover:brightness-105 transition-all text-left"
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── A proposta de mensagem ──
            Fica ENTRE a conversa e o compositor de propósito: é decisão, não
            recado. Rolando a conversa a proposta subiria e sumiria, e quem não
            lê muito perderia o único botão que faz a campanha andar.

            O texto aqui evita a palavra "template" onde dá. Quem usa isso não
            precisa aprender o vocabulário da Meta pra mandar uma mensagem.

            Era uma moldura de 4% de tinta com três parágrafos cinzas empilhados
            embaixo da prévia; virou UMA bandeja em relevo (neu-card) com a
            prévia sobre o papel de parede do WhatsApp, os avisos comprimidos em
            pastilhas e um botão que finalmente PARECE o botão principal — antes
            ele usava .neu-send, que pinta a base cinza por cima do verde. */}
        {passo === 'proposta' && prop && atual && (() => {
          const arte = prop.cabecalho ? atual.anexos.find(a => a.id === prop.cabecalho!.anexoId) : null
          return (
          <div className="px-4 pb-2 shrink-0 min-h-0">
            {/* O cartão tem TETO e a decisão fica fora da parte que rola.
                O teto é em `vh` e não em `%`: porcentagem se resolve contra a
                altura do PAI, e o pai aqui tem altura automática — o `max-h:34%`
                era simplesmente ignorado, e o cartão voltava a 707px.
                Sem o teto, este cartão sozinho comia 380px e a conversa ficava
                com 97px; com o cartão inteiro rolando, o botão "Pode mandar pra
                aprovação" saía da vista e a campanha parava sem motivo visível.
                Então: corpo rola, botões sempre à mostra. */}
            <div className={`max-w-3xl mx-auto rounded-2xl neu-card p-3.5 flex flex-col ${estreito ? 'max-h-[30vh]' : 'max-h-[34vh]'}`}>
              <div className="min-h-0 overflow-y-auto fio-rolagem pr-1 -mr-1">
              <div className="flex items-center gap-2 mb-2.5">
                <FiEdit3 className="w-3.5 h-3.5 shrink-0 text-primary" />
                <span className="text-[12.5px] font-semibold">Escrevi esta mensagem pra você</span>
                {/* No celular esta frase não cabe (cortava 43px) — e ali a
                    prévia JÁ está num celular. Some sem perda. */}
                {!estreito && <span className="text-[11px] text-muted-foreground truncate">— é assim que ela chega no celular</span>}
                <button
                  onClick={() => setPropGuardada(true)}
                  title="Guardar a mensagem na ficha — ela continua ali, e a conversa fica maior"
                  className="ml-auto shrink-0 w-6 h-6 rounded-lg neu-item flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <FiMinus className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Prévia sobre o papel de parede do WhatsApp: o mais perto do
                  celular dela que dá pra chegar sem sair do painel. */}
              {/* O teto fica na PRÉVIA, não no cartão: com o cartão inteiro rolando,
                  o botão "Pode mandar pra aprovação" saía da vista e a campanha
                  parava sem ninguém entender por quê. A mensagem é o que pode
                  ser longo; a decisão fica sempre à vista. */}
              <div className="neu-thread rounded-2xl p-3">
                <div className="mx-auto max-w-[380px] rounded-xl rounded-tl-md neu-bubble bg-[#d9fdd3] dark:bg-[#005c4b] p-1.5 text-[#111b21] dark:text-[#e9edef]">
                  {prop.cabecalho && (
                    arte?.url
                      ? (/^video\//.test(arte.mime || '')
                          ? <video src={arte.url} controls muted playsInline preload="metadata" className="w-full rounded-lg mb-1.5 max-h-36 object-cover" />
                          : <img src={arte.url} alt="" className="w-full rounded-lg mb-1.5 max-h-36 object-cover" />)
                      : <div className="rounded-lg mb-1.5 px-2 py-5 text-[10.5px] text-center bg-black/10 dark:bg-white/10">a arte anexada entra aqui</div>
                  )}
                  <div className="px-1.5 pt-0.5 text-[12.5px] leading-relaxed whitespace-pre-wrap">
                    {corpoComPastilhas(prop.corpo)}
                  </div>
                  {prop.rodape && (
                    <div className="px-1.5 mt-1.5 text-[10.5px] opacity-60">{prop.rodape}</div>
                  )}
                  <div className="px-1.5 pb-0.5 mt-1 flex justify-end items-center gap-1 text-[10.5px] opacity-50">
                    {agora} <FiCheck className="w-2.5 h-2.5" />
                  </div>
                  {!!prop.botoes.length && (
                    <div className="mt-1.5 pt-1 border-t border-black/10 dark:border-white/15 space-y-0.5">
                      {prop.botoes.map((b, i) => (
                        <div key={i} className="text-center text-[11.5px] font-medium text-[#0a7cff] dark:text-[#53bdeb] py-1">{b.texto}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tudo que antes eram três parágrafos de 10px vira uma fila de
                  pastilhas: o que a Meta vai cobrar, se leva arte, e quantos
                  dados mudam por pessoa. Uma linha, não três blocos. */}
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span className={`neu-tag rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                  prop.categoria === 'UTILITY' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {prop.categoria === 'UTILITY' ? 'aviso de utilidade · tarifa menor' : 'marketing · tarifa promocional'}
                </span>
                {!!prop.cabecalho && <span className="neu-chip rounded-full px-2 py-0.5 text-[10.5px]">com a arte no topo</span>}
                {/* O botão nunca aponta direto pro destino: passa pelo nosso
                    /l/{{1}}, e é isso que faz o clique voltar com o NOME de quem
                    clicou. A pastilha diz o destino porque é o que ela reconhece
                    — o link de rastreio não significa nada pra quem usa. */}
                {prop.botoes.some(b => b.tipo === 'URL') && (
                  <span
                    className="neu-chip rounded-full px-2 py-0.5 text-[10.5px] max-w-[280px] truncate"
                    title={atual.ficha.destino ? `O botão abre ${atual.ficha.destino.url}. O clique passa pelo nosso link, então você vê quem clicou — e dá pra trocar o endereço depois do disparo sem refazer a mensagem na Meta.` : undefined}
                  >
                    botão abre {atual.ficha.destino ? atual.ficha.destino.url.replace(/^https:\/\//, '') : 'o endereço combinado'} · clique rastreado
                  </span>
                )}
                {prop.variaveis > 0 && (
                  <span className="neu-chip rounded-full px-2 py-0.5 text-[10.5px]">
                    {prop.variaveis === 1 ? '1 dado por pessoa' : `${prop.variaveis} dados por pessoa`}
                  </span>
                )}
              </div>

              {/* Todo conserto que o servidor fez aparece aqui. Corrigir calado é
                  o que fez o cliente do LOGMG receber template sem os botões que
                  pediu — com a IA ainda falando deles. */}
              {!!prop.avisos.length && (
                <ul className="mt-2.5 space-y-1 rounded-xl neu-tag px-2.5 py-2 text-amber-700 dark:text-amber-400">
                  {prop.avisos.map((a, i) => (
                    <li key={i} className="text-[10.5px] leading-snug flex gap-1.5">
                      <span className="shrink-0">•</span><span>{a}</span>
                    </li>
                  ))}
                </ul>
              )}

              </div>

              <div className="mt-3 flex items-center gap-2 shrink-0">
                <button
                  onClick={confirmaTemplate}
                  disabled={!!tplOcupado}
                  className="flex-1 h-10 rounded-xl bg-primary text-primary-foreground text-[13px] font-semibold neu-relief hover:brightness-110 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {tplOcupado === 'confirmando'
                    ? <><FiLoader className="w-4 h-4 animate-spin" /> Enviando pra Meta…</>
                    : <><FiCheck className="w-4 h-4" /> Pode mandar pra aprovação</>}
                </button>
                <button
                  onClick={descartaTemplate}
                  disabled={!!tplOcupado}
                  className="h-10 px-3.5 rounded-xl neu-item text-[12.5px] text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
                >
                  Escreve outro
                </button>
              </div>
              <div className="mt-2 text-[10.5px] text-muted-foreground">
                A Meta aprova antes de a mensagem sair — de minutos a algumas horas.
              </div>
            </div>
          </div>
          )
        })()}

        {/* ── Mandar o teste ──
            O prompt do agente promete este botão assim que ele grava o número.
            Sem ele renderizado, o agente mandava a pessoa procurar, ela não
            achava e ele mandava recarregar a página — sem saída. Se um dia o
            botão sair daqui, a linha correspondente TEM que sair do prompt.

            Só aparece com template APROVADO: a rota recusa o resto, e oferecer
            um botão que responde 409 é a mesma armadilha de novo. */}
        {(passo === 'teste' || passo === 'disparo') && atual?.ficha?.teste?.numero && atual.ficha.template?.status === 'APPROVED' && !atual.disparada && (
          <div className="px-4 pb-2 shrink-0">
            <div className="max-w-3xl mx-auto rounded-xl neu-card px-3 py-2.5 flex items-center gap-3">
              <FiSmartphone className="w-4 h-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1 text-[11.5px] leading-snug">
                {atual.ficha.teste.enviadoEm ? (
                  <>Teste enviado para <b>{atual.ficha.teste.comoFoiDito}</b>. Não chegou? Confira o número e mande de novo.</>
                ) : (
                  <>Antes de mandar pra todo mundo, veja no seu celular: mando esta mensagem só para <b>{atual.ficha.teste.comoFoiDito}</b>.</>
                )}
              </div>
              <button
                onClick={mandaTeste}
                disabled={!!tplOcupado}
                className={`shrink-0 h-8 px-3 rounded-lg text-[11.5px] font-medium transition-colors flex items-center gap-1.5 ${
                  atual.ficha.teste.enviadoEm
                    ? 'text-primary hover:bg-primary/10'
                    : 'bg-primary text-primary-foreground neu-relief hover:bg-primary/90'
                } disabled:opacity-50`}
              >
                {tplOcupado === 'testando'
                  ? <><FiLoader className="w-3.5 h-3.5 animate-spin" /> Mandando…</>
                  : <><FiSend className="w-3.5 h-3.5" /> {atual.ficha.teste.enviadoEm ? 'Mandar de novo' : 'Mandar o teste'}</>}
              </button>
            </div>
            {!!atual.testes?.length && !!atual.testes[atual.testes.length - 1].avisos?.length && (
              <div className="max-w-3xl mx-auto mt-1 space-y-0.5">
                {atual.testes[atual.testes.length - 1].avisos.map((a, i) => (
                  <div key={i} className="text-[10.5px] text-amber-700 dark:text-amber-400">• {a}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Os que já foram. Enquanto a Meta não responde, esta faixa é a única
            coisa que diz que alguma coisa está acontecendo. */}
        {passo === 'aprovacao' && !!atual?.templatesCriados?.length && (
          <div className="px-4 pb-2 shrink-0">
            <div className="max-w-3xl mx-auto space-y-1.5">
              {atual.templatesCriados.map(t => (
                <div key={t.metaId} className="flex items-center gap-2.5 rounded-xl neu-card px-3 py-2 text-[11.5px]">
                  {t.status === 'APPROVED' ? <FiCheck className="w-4 h-4 shrink-0 text-emerald-500" />
                    : t.status === 'REJECTED' ? <FiAlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                    : <FiClock className="w-4 h-4 shrink-0 text-amber-500" />}
                  <span className="min-w-0 flex-1 leading-snug">
                    {t.status === 'APPROVED' ? <>
                        <b>Aprovada!</b> A mensagem “{t.nome}” já pode ser usada na campanha.
                        {t.categoriaFinal === 'MARKETING' && t.categoria === 'UTILITY' && (
                          <span className="text-muted-foreground"> A Meta classificou como marketing, então cada envio custa a tarifa promocional.</span>
                        )}
                        {t.categoriaFinal === 'UTILITY' && (
                          <span className="text-emerald-600 dark:text-emerald-400"> Ficou como utilidade — a tarifa mais barata.</span>
                        )}
                      </>
                      : t.status === 'REJECTED' ? <><b>A Meta não aceitou</b> a mensagem “{t.nome}”.{t.motivoRejeicao ? ` Motivo: ${t.motivoRejeicao}.` : ''} Peça outra ao agente.</>
                      : <>A Meta está analisando a mensagem “{t.nome}”.</>}
                  </span>
                  {t.status !== 'APPROVED' && t.status !== 'REJECTED' && (
                    <div className="shrink-0 flex items-center gap-2">
                      {/* Quando foi a última pergunta à Meta. Sem isto o botão é
                          clique no escuro: apertar de novo dá exatamente a mesma
                          tela, e não dá pra saber se travou ou se a Meta é que
                          ainda não respondeu. */}
                      <span
                        className={`text-[10.5px] whitespace-nowrap ${atual.templateChecagemErro ? 'text-red-500' : 'text-muted-foreground'}`}
                        title={atual.templateChecadoEm ? new Date(atual.templateChecadoEm).toLocaleString('pt-BR') : undefined}
                      >
                        {tplOcupado === 'checando' ? 'perguntando à Meta…'
                          : atual.templateChecagemErro ? `não consegui falar com a Meta ${desdeQuando(atual.templateChecadoEm) || ''}`
                          : atual.templateChecadoEm ? `conferido ${desdeQuando(atual.templateChecadoEm)}`
                          : 'ainda não conferi'}
                      </span>
                      <button
                        onClick={checaStatusTemplate}
                        disabled={!!tplOcupado}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors"
                        title="Pergunta à Meta agora. Ela também é perguntada sozinha a cada 90 segundos com a aba aberta."
                      >
                        <FiRefreshCw className={`w-3 h-3 ${tplOcupado === 'checando' ? 'animate-spin' : ''}`} />
                        já saiu?
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── O disparo ──
            Ele mora AQUI, embaixo da conversa que montou a campanha. Antes de
            01/09/2026 esta tela preparava tudo e mandava a pessoa disparar em
            Meta ADS: quem chegou até aqui simplesmente não atravessava, e o
            agente — que estava certo, o botão não existia nesta tela — dizia
            "o disparo não existe aqui". Meta ADS continua sendo onde os números
            aparecem depois; o gatilho é este.

            DOIS cliques, sempre. O primeiro só mostra a conta; o segundo manda.
            Se um dia isto virar um botão só, volta a ser possível gastar o
            dinheiro dela com um clique errado — e não há desfazer. Se este bloco
            sair da tela, as linhas dele TÊM que sair do prompt do agente. */}
        {passo === 'disparo' && atual && (
          <div className="px-4 pb-2 shrink-0">
            <div className="max-w-3xl mx-auto rounded-xl neu-card overflow-hidden">
              {!disparo ? (
                <div className="px-3 py-2.5 flex items-center gap-3">
                  <FiTarget className="w-4 h-4 shrink-0 text-emerald-500" />
                  <div className="min-w-0 flex-1 text-[11.5px] leading-snug">
                    {(atual.disparos?.length ?? 0) > 0 ? (
                      <><b>Falta o resto da lista.</b> {atual.disparos!.length === 1 ? 'O lote 1 já saiu' : `Já saíram ${atual.disparos!.length} lotes`} desta campanha — ninguém recebe duas vezes.</>
                    ) : (
                      <><b>Tudo pronto.</b> A campanha está montada{atual.ficha.publico?.total ? <> e a lista tem {atual.ficha.publico.total} {atual.ficha.publico.total === 1 ? 'pessoa' : 'pessoas'}</> : null}. Confira quem recebe antes de mandar.</>
                    )}
                  </div>
                  <button
                    onClick={veQuemRecebe}
                    disabled={!!disparoOcupado || !!tplOcupado}
                    className="shrink-0 h-8 px-3 rounded-lg text-[11.5px] font-medium bg-emerald-600 text-white neu-relief hover:bg-emerald-500 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                  >
                    {disparoOcupado === 'contando'
                      ? <><FiLoader className="w-3.5 h-3.5 animate-spin" /> Conferindo…</>
                      : <><FiUsers className="w-3.5 h-3.5" /> Ver quem vai receber</>}
                  </button>
                </div>
              ) : (
                <div className="px-3 py-2.5">
                  {/* A conta primeiro, grande. É o único número que importa aqui:
                      quantas mensagens pagas vão sair agora. */}
                  <div className="flex items-baseline gap-2">
                    <span className="text-[22px] font-semibold leading-none">{disparo.vao}</span>
                    <span className="text-[12px] font-medium">{disparo.vao === 1 ? 'pessoa recebe agora' : 'pessoas recebem agora'}</span>
                    {disparo.linhas > 0 && (
                      <span className="ml-auto text-[10.5px] text-muted-foreground">de {disparo.linhas} {disparo.linhas === 1 ? 'linha' : 'linhas'} na lista{disparo.lista ? ` “${disparo.lista}”` : ''}</span>
                    )}
                  </div>

                  {/* ── O PLANO DOS LOTES ──
                      A conta já era certa, mas a conclusão morava numa linha
                      âmbar de 10px: "635 pessoas ficam para o próximo lote — o
                      cartão volta quando a janela virar". Quem lê isso entende
                      que o resto sai sozinho. NÃO SAI: não existe agendamento
                      aqui, alguém precisa voltar e clicar.

                      Foi exatamente a dúvida da Nathany em 04/09 com uma lista
                      de 885 num número de 250/dia. A resposta inteira — quantos
                      hoje, quantas levas, quando volta, e que é ela quem manda —
                      fica no lugar onde ela decide. */}
                  {disparo.esperam > 0 && (
                    <div className="mt-2 rounded-lg px-2.5 py-2 text-[11px] leading-snug bg-amber-500/10 text-amber-800 dark:text-amber-300">
                      <b>A sua lista não sai de uma vez.</b>{' '}
                      {disparo.limite?.teto ? <>O número fala com {disparo.limite.teto} pessoas por dia{disparo.limite.usados > 0 && <> e {disparo.limite.usados} já {disparo.limite.usados === 1 ? 'saiu' : 'saíram'} hoje</>}, </> : null}
                      então saem <b>{disparo.vao}</b> agora e <b>{disparo.esperam}</b> {disparo.esperam === 1 ? 'fica' : 'ficam'} para depois
                      {disparo.limite?.teto ? <> — {Math.ceil(disparo.esperam / disparo.limite.teto) + 1} levas no total</> : null}.
                      <span className="block mt-1">
                        <b>O resto não sai sozinho.</b> Volte nesta conversa
                        {disparo.liberaEm
                          ? <> a partir de <b>{new Date(disparo.liberaEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</b></>
                          : <> quando a janela de 24h virar</>}
                        {' '}e clique em disparar de novo. A conversa fica aberta e ninguém recebe duas vezes.
                      </span>
                    </div>
                  )}

                  {/* Quem ficou de fora, item a item. "12 inválidos" obrigaria
                      ela a abrir o Excel pra descobrir que eram fixos. */}
                  {!!disparo.fora && Object.values(disparo.fora).some(n => n > 0) && (
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-muted-foreground">
                      {([
                        ['fixo', 'linha fixa'],
                        ['ddd', 'DDD que não existe'],
                        ['duvidoso', 'número duvidoso — confira à mão'],
                        ['lixo', 'sem telefone'],
                        ['repetido', 'repetida na lista — recebe uma vez'],
                        ['bloqueado', 'pediu pra não receber'],
                        ['semDado', `sem o dado de ${disparo.semDadoColunas.join(' / ') || 'uma coluna'}`],
                        ['jaRecebeu', 'já recebeu num lote anterior'],
                      ] as const).filter(([k]) => (disparo.fora?.[k] ?? 0) > 0).map(([k, rotulo]) => (
                        <span key={k}>− <b className="text-foreground">{disparo.fora![k]}</b> {rotulo}</span>
                      ))}
                    </div>
                  )}

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-muted-foreground">
                    {disparo.template && <span><FiTag className="inline w-3 h-3 mb-px" /> {disparo.template}</span>}
                    {disparo.destino && <span>o botão abre <b className="text-foreground">{disparo.destino}</b></span>}
                  </div>

                  {disparo.avisos.map((a, i) => (
                    <div key={i} className="mt-1.5 text-[10.5px] leading-snug text-amber-700 dark:text-amber-400">• {a}</div>
                  ))}

                  {/* Sem teste, ela tem que dizer que sabe. Um aviso que o botão
                      ignora é aviso decorativo — o servidor recusa sem isto. */}
                  {!disparo.testeEnviadoEm && (
                    <label className="mt-2 flex items-center gap-2 text-[10.5px] cursor-pointer select-none">
                      <input type="checkbox" checked={semTesteOk} onChange={e => setSemTesteOk(e.target.checked)} className="accent-amber-500" />
                      Sei que não testei e quero mandar assim mesmo.
                    </label>
                  )}

                  <div className="mt-2.5 flex items-center gap-2">
                    <button
                      onClick={disparaAgora}
                      disabled={!!disparoOcupado || (!disparo.testeEnviadoEm && !semTesteOk)}
                      className="h-8 px-3 rounded-lg text-[11.5px] font-medium bg-emerald-600 text-white neu-relief hover:bg-emerald-500 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                    >
                      {disparoOcupado === 'disparando'
                        ? <><FiLoader className="w-3.5 h-3.5 animate-spin" /> Disparando…</>
                        : <><FiSend className="w-3.5 h-3.5" /> Disparar para {disparo.vao} {disparo.vao === 1 ? 'pessoa' : 'pessoas'}</>}
                    </button>
                    <button
                      onClick={() => setDisparo(null)}
                      disabled={!!disparoOcupado}
                      className="h-8 px-3 rounded-lg text-[11.5px] text-muted-foreground hover:bg-muted/40 disabled:opacity-50 transition-colors"
                    >
                      Agora não
                    </button>
                    <span className="ml-auto text-[10px] text-muted-foreground">não dá pra desfazer depois de sair</span>
                  </div>
                </div>
              )}

              {/* Impedimento não é erro de sistema: é a conta dizendo por que
                  não dá. Fica no lugar do botão, não numa faixa vermelha longe. */}
              {!!disparo?.impedimentos.length && (
                <div className="px-3 py-2 border-t border-border bg-amber-500/10">
                  {disparo.impedimentos.map((m, i) => (
                    <div key={i} className="text-[11px] leading-snug text-amber-700 dark:text-amber-400 flex gap-1.5">
                      <FiAlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" /> {m}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── O compositor ──
            A BOX é o container, não o textarea — mesma gramática do painel Dados.
            O textarea fica transparente por dentro, e as ações moram nos cantos de
            baixo: clipe à esquerda, ação à direita. O anel de foco vai no
            container, pra caixa e ações acenderem como uma peça só.
            `neu-inset` não é enfeite: dentro de .neu-lab um `bg-background`
            renderiza MAIS CLARO que a base e vira um bloco branco na coluna.
            `block` no textarea tira o baseline da conta — sem ele o navegador
            reserva espaço pros descendentes e a caixa não encosta embaixo.

            Os chips de anexo agora moram DENTRO da caixa: eram mais uma faixa
            solta empilhada no rodapé (com opções e proposta já disputando ali),
            e o que estava anexado não parecia pertencer ao que você ia escrever.

            Sem campanha aberta o compositor não existe. Caixa desligada dizendo
            "crie uma campanha" é um beco: a saída está no botão do estado vazio. */}
        {/* Sem `glass-bar` nesta faixa. Ela pinta `hsl(--card / .85)` — branco
            translúcido — e o vidro só rende quando algo passa por baixo da
            barra. Aqui nada passa: a conversa tem rolagem própria e termina
            acima. Fica o filete de separação, e só. */}
        {atual && (
        <div className="px-4 pb-4 pt-2 border-t border-border shrink-0">
          <div className="max-w-3xl mx-auto">
            <input
              ref={arquivoRef}
              type="file"
              accept=".xlsx,.xls,.csv,image/*,audio/*,video/mp4"
              className="hidden"
              onChange={e => { recebeArquivo(e.target.files?.[0]); if (arquivoRef.current) arquivoRef.current.value = '' }}
            />

            <div className={`relative rounded-2xl bg-background border border-border neu-inset transition-shadow ${
              focado ? 'ring-2 ring-ring/40' : ''
            } ${atual.disparada ? 'opacity-50' : ''}`}>
              {!!atual.anexos?.length && (
                <div className="flex flex-wrap gap-1.5 px-2.5 pt-2.5">
                  {atual.anexos.map(a => (
                    <span key={a.id} className={`flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg neu-tag text-[11px] ${a.origem === 'draco' ? 'text-primary' : 'text-muted-foreground'}`}>
                      {a.origem === 'draco' ? <FiDatabase className="w-3 h-3 shrink-0" />
                        : a.tipo === 'planilha' ? <FiGrid className="w-3 h-3 shrink-0" />
                        : <FiImage className="w-3 h-3 shrink-0" />}
                      <span className="truncate max-w-[200px] text-foreground" title={a.nome}>{a.nome}</span>
                      {a.tipo === 'planilha' && <span className="shrink-0 font-medium">{a.total} tel.</span>}
                      {a.tipo === 'planilha' && (
                        <button
                          onClick={() => baixaLista(a)}
                          title="Baixar a lista inteira em Excel — com quem recebe, quem ficou de fora e por quê"
                          className="p-0.5 rounded hover:text-primary transition-colors"
                        >
                          <FiDownload className="w-3 h-3" />
                        </button>
                      )}
                      {a.tipo === 'imagem' && a.leitura && <FiEye className="w-3 h-3 shrink-0 text-emerald-500" title="li o que está escrito na peça" />}
                      <button
                        onClick={() => removeAnexo(a.id)}
                        title="Tirar da conversa"
                        className="p-0.5 rounded hover:text-red-500 transition-colors"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <textarea
                ref={campoRef}
                value={entrada}
                onChange={e => setEntrada(e.target.value)}
                onFocus={() => setFocado(true)}
                onBlur={() => setFocado(false)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envia() } }}
                // Colar imagem (Ctrl+V) é o caminho mais curto pra quem baixou a
                // arte e não quer procurar o arquivo.
                onPaste={e => {
                  const arq = Array.from(e.clipboardData.files)[0]
                  if (arq) { e.preventDefault(); recebeArquivo(arq) }
                }}
                disabled={atual.disparada || enviando || recState !== 'idle'}
                rows={2}
                style={{ maxHeight: TETO_CAIXA }}
                placeholder={
                  atual.disparada ? 'Campanha já disparada — esta conversa está fechada'
                    : 'Conte o que a campanha precisa dizer, ou cole a arte aqui…'
                }
                className="block w-full resize-none overflow-y-auto bg-transparent px-3.5 py-3 pl-11 pr-12 text-[12.5px] rounded-2xl focus:outline-none disabled:cursor-not-allowed placeholder:text-muted-foreground/60 leading-relaxed fio-rolagem"
              />

              <div className="absolute left-2 bottom-2">
                <button
                  onClick={() => arquivoRef.current?.click()}
                  disabled={atual.disparada || anexando}
                  title="Anexar planilha, imagem ou áudio — também dá pra arrastar ou colar"
                  className="w-8 h-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-40 transition-colors flex items-center justify-center"
                >
                  {anexando ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPaperclip className="w-4 h-4" />}
                </button>
              </div>

              {/* Uma ação só, que troca de cara: parar de gravar, transcrevendo,
                  enviar, ou o microfone quando não há o que enviar.
                  O critério é o TEXTO, não o foco: a caixa recebe foco sozinha ao
                  abrir a campanha e depois de cada anexo, então "focado" deixava o
                  microfone inalcançável — havia um botão de enviar apagado no lugar
                  dele o tempo todo. Vazio = microfone; digitou = enviar. */}
              <div className="absolute right-2 bottom-2">
                {recState === 'recording' ? (
                  <button onClick={pararGravacao}
                    className="w-8 h-8 rounded-full neu-send-danger bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center justify-center"
                    title="Parar e transcrever">
                    <FiSquare className="w-3.5 h-3.5 animate-pulse" />
                  </button>
                ) : recState === 'transcribing' ? (
                  <button disabled
                    className="w-8 h-8 rounded-full neu-send bg-primary/60 text-primary-foreground flex items-center justify-center cursor-wait"
                    title="Transcrevendo…">
                    <FiLoader className="w-4 h-4 animate-spin" />
                  </button>
                ) : (entrada.trim() || !podeFalar) ? (
                  <button onClick={() => envia()} disabled={atual.disparada || enviando || !entrada.trim()}
                    className="w-8 h-8 rounded-full bg-primary text-primary-foreground neu-send hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    title="Enviar (Enter · Shift+Enter quebra linha)">
                    <FiSend className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={gravar} disabled={atual.disparada || enviando}
                    className="w-8 h-8 rounded-full bg-primary text-primary-foreground neu-send hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    title="Falar em vez de digitar — o áudio é transcrito e enviado">
                    <FiMic className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* A dica só existia como `title` do botão de enviar — ou seja, em
                lugar nenhum. Em 04/09 a Nathany escreveu "escreve outro assim:"
                e o Enter mandou antes de ela colar o texto; o agente respondeu
                que a mensagem tinha cortado. Aparece quando ela está escrevendo,
                que é quando importa, e some no repouso pra não custar altura. */}
            {(focado || entrada) && recState === 'idle' && (
              <div className="mt-1 text-[10px] text-muted-foreground/80 text-right">
                <b>Enter</b> envia · <b>Shift+Enter</b> quebra a linha
              </div>
            )}

            {(recState !== 'idle' || recErro) && (
              <div className={`mt-1.5 text-[10.5px] flex items-center gap-1.5 ${recErro || recState === 'recording' ? 'text-red-500' : 'text-muted-foreground'}`}>
                {recErro ? (<><FiX className="w-3 h-3 shrink-0" /> {recErro}</>)
                  : recState === 'recording' ? (<><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" /> Gravando — {recSecs}s · toque no quadrado pra enviar</>)
                  : (<><FiLoader className="w-3 h-3 animate-spin shrink-0" /> Transcrevendo…</>)}
              </div>
            )}
          </div>
        </div>
        )}
      </section>

      {/* ── Coluna 3: a Ficha ──
          Era uma pilha de cinco caixas do mesmo tamanho, três delas dizendo
          "ainda não", e embaixo um sexto bloco repetindo por extenso a MESMA
          lista de faltas. Agora: um medidor no topo (KPI vira chip no título),
          UM próximo passo em destaque, e o mapa dos cinco campos em dois pesos.
          A ficha voltou a ser um progresso, não uma tabela. */}
      <aside className={estreito
        ? `absolute inset-y-0 right-0 z-30 w-[280px] max-w-[85%] bg-card border-l border-border flex flex-col min-h-0 shadow-2xl transition-transform ${gaveta === 'ficha' ? 'translate-x-0' : 'translate-x-full'}`
        : 'w-[260px] shrink-0 border-l border-border neu-ledge-l flex flex-col min-h-0'}>
        <header className="px-3.5 pt-3 pb-2.5 shrink-0 border-b border-border neu-ledge-b">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-semibold">Ficha da campanha</span>
            {atual && (
              <span className={`ml-auto shrink-0 neu-tag rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                atual.completa ? 'text-emerald-600 dark:text-emerald-400' : 'text-primary'
              }`}>
                {atual.completa ? 'pronta' : `${feitos} de ${totalFicha}`}
              </span>
            )}
          </div>
          {atual && (
            <div className="mt-2.5 h-1.5 w-full neu-gauge">
              <div
                className={`h-full rounded-full transition-all duration-500 ${atual.completa ? 'bg-emerald-500' : 'bg-primary'}`}
                style={{ width: `${atual.completa ? 100 : pct}%` }}
              />
            </div>
          )}
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto py-1">
          {!atual || !ficha ? (
            <>
              <div className="px-3.5 pt-2.5 pb-3 text-[11.5px] leading-relaxed text-muted-foreground">
                Abra uma campanha e a ficha começa a se preencher sozinha, conforme você conversa.
              </div>
              <div className="opacity-45">{mapaDaFicha}</div>
            </>
          ) : (
            <>
              {/* UM próximo passo, não uma lista de três. Quem usa isto lê
                  pouco: dizer as três coisas que faltam de uma vez é a mesma
                  informação com o triplo do peso — e sem indicar por onde
                  começar. O resto vira um contador atrás. */}
              {/* A mensagem que ela guardou. Fica no topo da Ficha, com o
                  começo do texto — uma linha que só diz "mensagem guardada"
                  obrigaria a abrir pra lembrar qual é. E o botão de aprovar
                  aparece aqui também: o gesto não pode depender de reabrir. */}
              {prop && propGuardada && (
                <div className="mx-3 mt-2.5 mb-1 rounded-xl neu-card px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-primary">
                    <FiEdit3 className="w-3 h-3" /> A mensagem que escrevi
                  </div>
                  <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground line-clamp-3">{prop.corpo}</p>
                  <div className="mt-2 flex items-center gap-1.5">
                    <button
                      onClick={() => setPropGuardada(false)}
                      className="h-7 px-2.5 rounded-lg neu-item text-[11px] font-medium"
                    >
                      Ver inteira
                    </button>
                    <button
                      onClick={confirmaTemplate}
                      disabled={!!tplOcupado}
                      className="h-7 px-2.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-semibold neu-relief hover:brightness-110 disabled:opacity-50 transition-all flex items-center gap-1"
                    >
                      {tplOcupado === 'confirmando'
                        ? <><FiLoader className="w-3 h-3 animate-spin" /> Enviando…</>
                        : <><FiCheck className="w-3 h-3" /> Pode aprovar</>}
                    </button>
                  </div>
                </div>
              )}

              {!atual.completa && atual.falta.length > 0 && (
                <div className="mx-3 mt-2.5 mb-1 rounded-xl neu-card px-3 py-2.5">
                  <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-amber-600 dark:text-amber-400">
                    Falta agora
                  </div>
                  <div className="mt-1 text-[13px] font-medium leading-snug first-letter:uppercase">
                    {rotuloFalta(atual.falta[0])}
                  </div>
                  <div className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground">
                    É só pedir na conversa ao lado.
                    {atual.falta.length > 1 && ` Depois ainda ${atual.falta.length === 2 ? 'vem mais 1 item' : `vêm mais ${atual.falta.length - 1} itens`}.`}
                  </div>
                </div>
              )}

              {/* O disparo real continua no fluxo do painel — o chat prepara. Esta
                  nota existe pra a ficha "pronta" não parecer um botão que sumiu. */}
              {atual.completa && !atual.disparada && (
                <div className="mx-3 mt-2.5 mb-1 rounded-xl neu-tag px-3 py-2.5 text-emerald-700 dark:text-emerald-300">
                  <div className="text-[13px] font-semibold">
                    {(atual.disparos?.length ?? 0) > 0 ? 'Falta o resto da lista.' : 'Tudo pronto.'}
                  </div>
                  <div className="mt-1 text-[10.5px] leading-snug text-muted-foreground">
                    O disparo é o cartão verde embaixo da conversa. Quando a lista inteira sair, esta
                    conversa passa para “Já disparadas” e os números aparecem em Meta ADS.
                  </div>
                </div>
              )}

              {mapaDaFicha}
            </>
          )}
        </div>
      </aside>
    </div>
  )
}
